import React from 'react';
import cx from 'classnames';
import { useSelect } from 'downshift';

import { Icon } from 'js/components';

import styles from './AssessmentTypeSelect.module.scss';

function itemToString(item) {
  return item?.name ?? '';
}

/**
 * Picks the kind of assessment being created.
 *
 * A `useSelect` rather than a `useCombobox`, because the list is short and
 * closed: there is nothing useful to type. Each option carries a description,
 * since the difference between the two is about how the assessment is run
 * rather than anything the names convey on their own.
 */
export default function AssessmentTypeSelect({
  id = 'type',
  types = [],
  value,
  onChange = () => {},
  onBlur = () => {},
  invalid = false,
  disabled = false,
}) {
  const selectedItem = types.find((type) => type.value === value) ?? null;

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect({
    items: types,
    selectedItem,
    itemToString,
    onSelectedItemChange: ({ selectedItem: next }) => {
      if (next) {
        onChange(next.value);
      }
    },
  });

  // The toggle is a readonly `form-control` rather than a button so it lines up
  // with the text inputs around it and picks up Bootstrap's invalid styling.
  // Downshift puts `type="button"` on it, which is meaningless on an input.
  const {
    type: _toggleType,
    onClick: onToggleClick,
    ...toggleProps
  } = getToggleButtonProps({ id, disabled, onBlur });

  return (
    <div className={cx('combobox', styles.root)}>
      <div
        className="input-group"
        onClick={disabled ? undefined : onToggleClick}
      >
        <input
          readOnly
          className={cx('form-control', styles.toggle, {
            'is-invalid': invalid,
          })}
          placeholder="Choose a type"
          value={itemToString(selectedItem)}
          aria-invalid={invalid}
          {...toggleProps}
        />
        <div className="input-group-append">
          <span aria-hidden="true">
            <Icon icon={['far', isOpen ? 'chevron-up' : 'chevron-down']} />
          </span>
        </div>
      </div>
      <ul
        {...getMenuProps()}
        className={cx('combobox-items', styles.menu)}
        style={!isOpen ? { display: 'none' } : {}}
      >
        {isOpen &&
          types.map((item, index) => (
            <li
              key={item.value}
              className={cx('combobox-item', styles.item, {
                highlighted: highlightedIndex === index,
                selected: selectedItem?.value === item.value,
              })}
              {...getItemProps({ item, index })}
            >
              <span className={styles['item-name']}>{item.name}</span>
              <span className={styles['item-description']}>
                {item.description}
              </span>
            </li>
          ))}
      </ul>
    </div>
  );
}
